import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AtSign, Coins, Film, ImagePlus, Loader2, MessageSquare, Mic, Music, Plus, RotateCcw, Send, Sparkles, Square, X } from 'lucide-react';
import { creditChip, formatChatCredits, generationCreditChip } from '../../lib/chatCredits';
import { askFieldsForDisplay, askPromptForDisplay, DIRECT_VIDEO_NOTE, keyframeAdviceDeclines, type CanvasChatMessage } from '../../lib/canvasAgent';

type Props = {
  messages: CanvasChatMessage[];
  sending: boolean;
  recording: boolean;
  recordSeconds: number;
  balance: number;
  suggestions: string[];
  onSend: (text: string) => void;
  onToggleMic: () => void;
  onCancelMic: () => void;
  onAttach: () => void;
  /** Set when open questions can no longer be answered (e.g. board emptied). */
  askBlockedReason?: string;
  onAskChange: (messageId: string, fieldId: string, value: string) => void;
  onAskConfirm: (messageId: string) => void;
  /** Reopens the debit confirmation attached to a reply. */
  onReopenDebit?: (messageId: string) => void;
  /** Opens the Explore modal prefilled from this reply. */
  onOpenExplore?: (messageId: string) => void;
  /** Continues the normal script instead of the Explore example. */
  onDeclineExplore?: (messageId: string) => void;
  /** Selected board nodes; the agent reads them as what the request is about. */
  references?: PromptReference[];
  onRemoveReference?: (id: string) => void;
  onClearReferences?: () => void;
  /** Fires when the conversation docks to the right edge (full height) or returns to the center. */
  onDockChange?: (docked: boolean) => void;
  /** Current docked width, in px. The parent clamps it. */
  chatWidth?: number;
  onChatWidthChange?: (width: number) => void;
  /** Conversations of this project. The active one is `messages`. */
  chats?: Array<{ id: string; title: string }>;
  activeChatId?: string;
  onNewChat?: () => void;
  onSelectChat?: (id: string) => void;
  onCloseChat?: (id: string) => void;
  /** A reply is still landing; switching would detach it from this chat. */
  chatsLocked?: boolean;
  newChatDisabled?: boolean;
  /** Shown on the new-chat button. */
  newChatHint?: string;
};

export type PromptReference = { id: string; label: string; kind: 'image' | 'video' | 'audio'; url?: string };

export type CanvasPromptHandle = { focus: () => void };

const CanvasPrompt = forwardRef<CanvasPromptHandle, Props>(function CanvasPrompt(props, ref) {
  const { messages, sending, recording, recordSeconds, balance, suggestions } = props;
  const [text, setText] = useState('');
  /** Side panel visibility; closing it brings the composer back to the center. */
  const [open, setOpen] = useState(true);
  const threadRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const resizeRef = useRef<{ x: number; w: number } | null>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const chatCount = props.chats?.length || 0;
  const docked = open && (messages.length > 0 || chatCount > 1);
  const { onDockChange } = props;
  useEffect(() => {
    onDockChange?.(docked);
  }, [docked, onDockChange]);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }), []);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  // A new reply while the panel is closed reopens it so the answer is visible.
  const lastId = messages[messages.length - 1]?.id;
  useEffect(() => {
    if (lastId) setOpen(true);
  }, [lastId]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [text]);

  useEffect(() => {
    setText('');
  }, [props.activeChatId]);

  useEffect(() => {
    const root = tabsRef.current;
    if (!root || !props.activeChatId) return;
    root.querySelector<HTMLElement>(`[data-chat-id="${CSS.escape(props.activeChatId)}"]`)?.scrollIntoView?.({ inline: 'nearest', block: 'nearest' });
  }, [props.activeChatId, chatCount]);

  const submit = () => {
    const value = text.trim();
    if (!value || sending || recording) return;
    props.onSend(value);
    setText('');
    setOpen(true);
  };

  return (
    <div className={`tb-prompt${docked ? ' is-docked' : ''}`} onPointerDown={(e) => e.stopPropagation()}>
      {(messages.length > 0 || chatCount > 1) && !open && (
        <div className="tb-chat-reopen-row">
          <button
            type="button"
            className="tb-chat-reopen"
            onClick={() => setOpen(true)}
            aria-label="Abrir a conversa"
            title="Abrir a conversa"
          >
            <MessageSquare size={13} /> {chatCount > 1 ? `Conversas · ${chatCount}` : `Conversa · ${messages.length}`}
          </button>
          {props.onNewChat && (
            <button
              type="button"
              className="tb-chat-reopen"
              onClick={props.onNewChat}
              disabled={props.chatsLocked || props.newChatDisabled}
              aria-label="Novo chat"
              title={props.newChatHint || 'Começar um chat zerado'}
            >
              <Plus size={13} /> Novo chat
            </button>
          )}
        </div>
      )}
      {docked && (
        <div
          className="tb-chat-resize"
            role="slider"
            aria-orientation="vertical"
            aria-label="Redimensionar a conversa"
            aria-valuemin={320}
            aria-valuemax={640}
            aria-valuenow={props.chatWidth}
            tabIndex={0}
            title="Arraste para redimensionar"
            onPointerDown={(e) => {
              if (e.button !== 0 || !props.onChatWidthChange) return;
              e.preventDefault();
              e.stopPropagation();
              e.currentTarget.setPointerCapture(e.pointerId);
              resizeRef.current = { x: e.clientX, w: props.chatWidth ?? 440 };
              document.body.classList.add('tb-chat-dragging');
            }}
            onPointerMove={(e) => {
              const drag = resizeRef.current;
              if (!drag) return;
              props.onChatWidthChange?.(drag.w + (drag.x - e.clientX));
            }}
            onPointerUp={() => {
              if (!resizeRef.current) return;
              resizeRef.current = null;
              document.body.classList.remove('tb-chat-dragging');
            }}
            onPointerCancel={() => {
              resizeRef.current = null;
              document.body.classList.remove('tb-chat-dragging');
            }}
            onKeyDown={(e) => {
              const step = e.shiftKey ? 40 : 16;
              if (e.key === 'ArrowLeft') { e.preventDefault(); props.onChatWidthChange?.((props.chatWidth ?? 440) + step); }
              if (e.key === 'ArrowRight') { e.preventDefault(); props.onChatWidthChange?.((props.chatWidth ?? 440) - step); }
            }}
        />
      )}
      {docked && (
        <div className="tb-thread-wrap">
          <div className="tb-chat-head">
            {chatCount > 0 ? (
              <div className="tb-chat-tabs" ref={tabsRef} role="tablist" aria-label="Conversas">
                {props.chats!.map((chat) => {
                  const active = chat.id === props.activeChatId;
                  return (
                    <div key={chat.id} className={`tb-chat-tab${active ? ' is-active' : ''}`}>
                      <button
                        type="button"
                        role="tab"
                        data-chat-id={chat.id}
                        className="tb-chat-tab-label"
                        aria-selected={active}
                        title={chat.title}
                        disabled={props.chatsLocked && !active}
                        onClick={() => props.onSelectChat?.(chat.id)}
                      >
                        {chat.title}
                      </button>
                      {chatCount > 1 && (
                        <button
                          type="button"
                          className="tb-chat-tab-x"
                          aria-label={`Fechar ${chat.title}`}
                          title="Fechar este chat"
                          disabled={props.chatsLocked}
                          onClick={() => props.onCloseChat?.(chat.id)}
                        >
                          <X size={10} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className="tb-chat-title"><MessageSquare size={13} /> Conversa</span>
            )}
            {props.onNewChat && (
              <button
                type="button"
                className="tb-chat-new"
                onClick={props.onNewChat}
                disabled={props.chatsLocked || props.newChatDisabled}
                aria-label="Novo chat"
                title={props.newChatHint || 'Começar um chat zerado'}
              >
                <Plus size={13} /> Novo
              </button>
            )}
            <button
              type="button"
              className="tb-chat-head-btn"
              onClick={() => setOpen(false)}
              aria-label="Fechar a conversa"
              title="Fechar a conversa (o campo volta para o centro)"
            >
              <X size={14} />
            </button>
          </div>
          <div className="tb-thread" ref={threadRef}>
            {messages.length === 0 && chatCount > 1 && (
              <p className="tb-thread-empty">Chat novo. As outras conversas ficam nas abas.</p>
            )}
            {messages.map((msg, i) => msg.contextBreak ? (
              <div key={msg.id} className="tb-context-break" role="separator">
                <RotateCcw size={10} /> {msg.text || 'Quadro recomeçado — o agente esqueceu o fluxo anterior'}
              </div>
            ) : (
              <Bubble
                key={msg.id}
                msg={msg}
                blockedReason={props.askBlockedReason}
                onAskChange={(field, value) => props.onAskChange(msg.id, field, value)}
                onAskConfirm={() => props.onAskConfirm(msg.id)}
                onReopenDebit={() => props.onReopenDebit?.(msg.id)}
                onOpenExplore={() => props.onOpenExplore?.(msg.id)}
                onDeclineExplore={() => props.onDeclineExplore?.(msg.id)}
                showSuggestions={i === messages.length - 1}
                suggestionsDisabled={sending || recording}
                onSuggestion={props.onSend}
              />
            ))}
            {sending && (
              <div className="tb-bubble tb-bubble--assistant tb-bubble--typing">
                <Loader2 size={12} className="spin" /> Pensando…
              </div>
            )}
          </div>
        </div>
      )}

      {messages.length === 0 && suggestions.length > 0 && (
        <div className="tb-suggestions">
          {suggestions.map((s) => (
            <button key={s} type="button" className="tb-suggestion" onClick={() => { setText(s); inputRef.current?.focus(); }}>
              {s}
            </button>
          ))}
        </div>
      )}

      {props.references && props.references.length > 0 && (
        <div className="tb-refs" role="group" aria-label="Referenciados no agente">
          <span className="tb-refs-title" title="O agente vai considerar estes itens no seu pedido">
            <AtSign size={10} /> Referenciando
          </span>
          {props.references.map((r) => (
            <span key={r.id} className="tb-ref-chip" title={r.label}>
              {r.url ? (
                <img src={r.url} alt="" draggable={false} />
              ) : (
                <span className="tb-ref-icon">{r.kind === 'audio' ? <Music size={9} /> : <Film size={9} />}</span>
              )}
              <span className="tb-ref-label">{r.label}</span>
              {props.onRemoveReference && (
                <button type="button" aria-label={`Tirar ${r.label} da referência`} title="Tirar da referência" onClick={() => props.onRemoveReference?.(r.id)}>
                  <X size={9} />
                </button>
              )}
            </span>
          ))}
          {props.references.length > 1 && props.onClearReferences && (
            <button type="button" className="tb-refs-clear" onClick={props.onClearReferences}>
              Limpar
            </button>
          )}
        </div>
      )}

      <form
        className={`tb-composer${recording ? ' is-recording' : ''}`}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <button type="button" className="tb-icon-btn" onClick={props.onAttach} title="Adicionar fotos ou vídeos (galeria ou upload)" aria-label="Adicionar fotos ou vídeos" disabled={recording}>
          <ImagePlus size={17} />
        </button>
        {recording ? (
          <div className="tb-recording">
            <span className="tb-rec-dot" />
            Gravando… {recordSeconds}s
            <button type="button" className="tb-rec-cancel" onClick={props.onCancelMic} aria-label="Cancelar gravação">
              <X size={13} />
            </button>
          </div>
        ) : (
          <textarea
            ref={inputRef}
            rows={1}
            value={text}
            placeholder="Peça algo ao agente ou grave um áudio…"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
        )}
        <button
          type="button"
          className={`tb-icon-btn${recording ? ' is-active' : ''}`}
          onClick={props.onToggleMic}
          disabled={sending}
          title={recording ? 'Parar e enviar áudio' : 'Falar com o agente'}
          aria-label={recording ? 'Parar e enviar áudio' : 'Gravar áudio'}
        >
          {recording ? <Square size={14} /> : <Mic size={17} />}
        </button>
        <button type="submit" className="tb-send-btn" disabled={!text.trim() || sending || recording} aria-label="Enviar">
          {sending ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
        </button>
      </form>

      <div className="tb-prompt-foot">
        <span style={creditChip}>
          <Coins size={10} style={{ marginRight: 4 }} />
          Saldo {balance.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} créditos
        </span>
      </div>
    </div>
  );
});

export default CanvasPrompt;

function Bubble({
  msg,
  blockedReason,
  onAskChange,
  onAskConfirm,
  onReopenDebit,
  onOpenExplore,
  onDeclineExplore,
  showSuggestions,
  suggestionsDisabled,
  onSuggestion,
}: {
  msg: CanvasChatMessage;
  blockedReason?: string;
  onAskChange: (fieldId: string, value: string) => void;
  onAskConfirm: () => void;
  onReopenDebit: () => void;
  onOpenExplore: () => void;
  onDeclineExplore: () => void;
  showSuggestions: boolean;
  suggestionsDisabled: boolean;
  onSuggestion: (text: string) => void;
}) {
  const mine = msg.role === 'user';
  const answers = msg.askAnswers || {};
  const declinedKeyframes = !msg.askResolved && keyframeAdviceDeclines(msg.ask, answers);
  const asksKeyframes = !declinedKeyframes && Boolean(msg.ask?.fields?.some((f) => f.id === 'keyframes'));
  const fields = declinedKeyframes
    ? []
    : asksKeyframes ? askFieldsForDisplay(msg.ask, answers) : (msg.ask?.fields || []);
  const promptText = declinedKeyframes
    ? DIRECT_VIDEO_NOTE
    : asksKeyframes ? askPromptForDisplay(msg.text, fields) : msg.text;
  const complete = fields.every((f) => answers[f.id] || f.options.length <= 1);
  const blocked = !msg.askResolved && Boolean(blockedReason);
  const explore = !mine && msg.exploreOffer && !msg.exploreOffer.resolved ? msg.exploreOffer : null;
  return (
    <div className={`tb-bubble ${mine ? 'tb-bubble--user' : 'tb-bubble--assistant'}${msg.error ? ' is-error' : ''}`}>
      {msg.audioSeconds != null && (
        <div className="tb-bubble-audio">
          <Mic size={10} /> {msg.audioSeconds.toFixed(1)}s
        </div>
      )}
      {msg.refs && msg.refs.length > 0 && (
        <div className="tb-bubble-refs" title="Referenciados neste pedido">
          <AtSign size={10} /> {msg.refs.join(', ')}
        </div>
      )}
      {promptText && <div className="tb-bubble-text">{promptText}</div>}
      {msg.busy && (
        <div className="tb-script-loading" role="status" aria-live="polite">
          <span className="tb-script-spin" aria-hidden="true"><i /><i /><i /></span>
          <span>{msg.status || 'Montando roteiro…'}</span>
        </div>
      )}
      {!msg.busy && msg.status && <div className="tb-bubble-status">{msg.status}</div>}
      {msg.debit && (
        <button
          type="button"
          className="tb-debit-reopen"
          disabled={msg.debit.done}
          onClick={onReopenDebit}
          title={msg.debit.done ? 'Débito já confirmado' : 'Abrir a confirmação de débito'}
        >
          {msg.debit.done ? 'Débito confirmado' : msg.debit.label}
        </button>
      )}

      {fields.length > 0 && (
        <div className="tb-ask">
          {msg.ask?.note && <div className="tb-ask-note">{msg.ask.note}</div>}
          {fields.map((field) => (
            <div key={field.id} className="tb-ask-field">
              <div className="tb-ask-label">{field.label}</div>
              <div className="tb-ask-options">
                {field.options.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={msg.askResolved || blocked}
                    className={`tb-ask-option${answers[field.id] === opt.value ? ' is-active' : ''}`}
                    onClick={() => onAskChange(field.id, opt.value)}
                  >
                    {opt.label}
                    {field.recommended === opt.value && <span className="tb-ask-rec">recomendado</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {blocked ? (
            <div className="tb-ask-blocked">{blockedReason}</div>
          ) : !msg.askResolved && (
            <button type="button" className="tb-ask-confirm" disabled={!complete} onClick={onAskConfirm}>
              Confirmar
            </button>
          )}
        </div>
      )}

      {explore && (
        <div className="tb-explore-actions">
          {explore.unusedImagesNote && (
            <p className="tb-explore-note">{explore.unusedImagesNote}</p>
          )}
          <button
            type="button"
            className="tb-bubble-suggestion"
            disabled={suggestionsDisabled}
            onClick={onOpenExplore}
          >
            Ver modelo
          </button>
          <button
            type="button"
            className="tb-explore-skip"
            disabled={suggestionsDisabled}
            onClick={onDeclineExplore}
          >
            Não usar o modelo
          </button>
        </div>
      )}

      {showSuggestions && !declinedKeyframes && !msg.busy && !mine && !fields.length && !explore && msg.suggestions?.length ? (
        <div className="tb-bubble-suggestions">
          {msg.suggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="tb-bubble-suggestion"
              disabled={suggestionsDisabled}
              onClick={() => onSuggestion(s)}
              title="Enviar este pedido"
            >
              <Sparkles size={10} /> {s}
            </button>
          ))}
        </div>
      ) : null}

      {(msg.credits != null || (msg.generationCredits != null && msg.generationCredits > 0)) && (
        <div className="tb-bubble-chips">
          {msg.generationCredits != null && msg.generationCredits > 0 && (
            <span style={generationCreditChip}>
              {msg.generationCredits} créditos ({msg.generationKind === 'image' ? 'imagem' : msg.generationKind === 'audio' ? 'som' : 'vídeo'})
            </span>
          )}
          {msg.credits != null && (
            <span
              style={creditChip}
              title={msg.inputTokens || msg.outputTokens ? `${msg.inputTokens || 0} inp · ${msg.outputTokens || 0} out` : undefined}
            >
              {formatChatCredits(msg.credits)} créditos (chat
              {msg.inputTokens || msg.outputTokens ? ` · ${msg.inputTokens || 0} inp / ${msg.outputTokens || 0} out` : ''}
              )
            </span>
          )}
        </div>
      )}
    </div>
  );
}
